import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { SCHOOLY_API_V2_URL } from '../config';
import {
  CreateTimetableRequest,
  CreateTimetableResponse,
  CreateTimetableStructureRequest,
  CreateTimetableStructureResponse,
  DeleteTimetableRequest,
  DeleteTimetableResponse,
  DeleteTimetableStructureRequest,
  DeleteTimetableStructureResponse,
  GetGroupScheduleRequest,
  GetGroupScheduleResponse,
  GetStaffScheduleRequest,
  GetStaffScheduleResponse,
  GetStudentScheduleRequest,
  GetStudentScheduleResponse,
  GetTimetableRequest,
  GetTimetableResponse,
  GetTimetablesRequest,
  GetTimetablesResponse,
  GetTimetableStructureRequest,
  GetTimetableStructureResponse,
  GetTimetableStructuresRequest,
  GetTimetableStructuresResponse,
  ModifyTimetableRequest,
  ModifyTimetableResponse,
  PerformTimetableActionRequest,
  PerformTimetableActionResponse,
  UpdateTimetableStructureRequest,
  UpdateTimetableStructureResponse,
} from './apiTypes/endpoints/timetables';
import { ApiError } from './apiTypes/misc';
import {
  createTimetablePath,
  createTimetableStructurePath,
  deleteTimetablePath,
  deleteTimetableStructurePath,
  getGroupSchedulePath,
  getStaffSchedulePath,
  getStudentSchedulePath,
  getTimetablePath,
  getTimetablesPath,
  getTimetableStructurePath,
  getTimetableStructuresPath,
  modifyTimetablePath,
  performTimetableActionPath,
  updateTimetableStructurePath,
} from './apiTypes/paths/timetables';
import { RQUseMutationOptions, RQUseMutationResult, RQUseQueryOptions } from './apiTypes/query';
import * as api from './requests';

const TIMETABLES_URL = '/timetabling/';

export const GET_TIMETABLE_STRUCTURES_QUERY = `${TIMETABLES_URL}GET_TIMETABLE_STRUCTURES_QUERY`;

export function getTimetableStructures(
  params: GetTimetableStructuresRequest,
): Promise<GetTimetableStructuresResponse> {
  return api.get(`${SCHOOLY_API_V2_URL}${getTimetableStructuresPath()}`, {
    params,
  });
}

export const useGetTimetableStructuresQuery = (
  params: GetTimetableStructuresRequest,
  options?: RQUseQueryOptions<GetTimetableStructuresResponse>,
) => {
  return useQuery<GetTimetableStructuresResponse, ApiError>(
    [GET_TIMETABLE_STRUCTURES_QUERY, params],
    () => getTimetableStructures(params),
    options,
  );
};

export const GET_TIMETABLE_STRUCTURE_QUERY = `${TIMETABLES_URL}GET_TIMETABLE_STRUCTURE_QUERY`;

export function getTimetableStructure({
  structureId,
}: GetTimetableStructureRequest): Promise<GetTimetableStructureResponse> {
  return api.get(
    `${SCHOOLY_API_V2_URL}${getTimetableStructurePath({ structure_id: structureId })}`,
  );
}

export const useGetTimetableStructureQuery = (
  params: GetTimetableStructureRequest,
  options?: RQUseQueryOptions<GetTimetableStructureResponse>,
) => {
  return useQuery<GetTimetableStructureResponse, ApiError>(
    [GET_TIMETABLE_STRUCTURE_QUERY, params],
    () => getTimetableStructure(params),
    options,
  );
};

export const CREATE_TIMETABLE_STRUCTURE_MUTATION = `${TIMETABLES_URL}CREATE_TIMETABLE_STRUCTURE_MUTATION`;

export function createTimetableStructure(
  data: CreateTimetableStructureRequest,
): Promise<CreateTimetableStructureResponse> {
  return api.post(`${SCHOOLY_API_V2_URL}${createTimetableStructurePath()}`, data);
}

export const useCreateTimetableStructureMutation = (
  options?: RQUseMutationOptions<CreateTimetableStructureResponse, CreateTimetableStructureRequest>,
): RQUseMutationResult<CreateTimetableStructureResponse, CreateTimetableStructureRequest> => {
  return useMutation(
    [CREATE_TIMETABLE_STRUCTURE_MUTATION],
    (params: CreateTimetableStructureRequest) => createTimetableStructure(params),
    options,
  );
};

export const UPDATE_TIMETABLE_STRUCTURE_MUTATION = `${TIMETABLES_URL}UPDATE_TIMETABLE_STRUCTURE_MUTATION`;

export function updateTimetableStructure({
  params,
  data,
}: UpdateTimetableStructureRequest): Promise<UpdateTimetableStructureResponse> {
  return api.patch(
    `${SCHOOLY_API_V2_URL}${updateTimetableStructurePath({ structure_id: params.structureId })}`,
    data,
  );
}

export const useUpdateTimetableStructureMutation = (
  options?: RQUseMutationOptions<UpdateTimetableStructureResponse, UpdateTimetableStructureRequest>,
): RQUseMutationResult<UpdateTimetableStructureResponse, UpdateTimetableStructureRequest> => {
  return useMutation(
    [UPDATE_TIMETABLE_STRUCTURE_MUTATION],
    (params: UpdateTimetableStructureRequest) => updateTimetableStructure(params),
    options,
  );
};

export const DELETE_TIMETABLE_STRUCTURE_MUTATION = `${TIMETABLES_URL}DELETE_TIMETABLE_STRUCTURE_MUTATION`;

export function deleteTimetableStructure({
  structureId,
}: DeleteTimetableStructureRequest): Promise<DeleteTimetableStructureResponse> {
  return api.remove(
    `${SCHOOLY_API_V2_URL}${deleteTimetableStructurePath({ structure_id: structureId })}`,
  );
}

export const useDeleteTimetableStructureMutation = (
  options?: RQUseMutationOptions<DeleteTimetableStructureResponse, DeleteTimetableStructureRequest>,
): RQUseMutationResult<DeleteTimetableStructureResponse, DeleteTimetableStructureRequest> => {
  return useMutation(
    [DELETE_TIMETABLE_STRUCTURE_MUTATION],
    (params: DeleteTimetableStructureRequest) => deleteTimetableStructure(params),
    options,
  );
};

export const GET_TIMETABLES_QUERY = `${TIMETABLES_URL}GET_TIMETABLES_QUERY`;

export function getTimetablesCacheKeys(params: GetTimetablesRequest) {
  return [GET_TIMETABLES_QUERY, params];
}

export function getTimetables(params: GetTimetablesRequest): Promise<GetTimetablesResponse> {
  return api.get(`${SCHOOLY_API_V2_URL}${getTimetablesPath()}`, { params });
}

export const useGetTimetablesQuery = (
  initialParams: GetTimetablesRequest,
  options?: RQUseQueryOptions<GetTimetablesResponse>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useQuery<GetTimetablesResponse, ApiError>(
    getTimetablesCacheKeys(params),
    () => getTimetables(params),
    options,
  );

  return { ...query, setParams, params };
};

export const GET_TIMETABLE_QUERY = `${TIMETABLES_URL}GET_TIMETABLE_QUERY`;

export function getTimetable({ timetableId }: GetTimetableRequest): Promise<GetTimetableResponse> {
  return api.get(`${SCHOOLY_API_V2_URL}${getTimetablePath({ timetable_id: timetableId })}`);
}

export function getTimetableCacheKeys(params: GetTimetableRequest) {
  return [GET_TIMETABLE_QUERY, params];
}

export const useGetTimetableQuery = (
  params: GetTimetableRequest,
  options?: RQUseQueryOptions<GetTimetableResponse>,
) => {
  return useQuery<GetTimetableResponse, ApiError>(
    [GET_TIMETABLE_QUERY, getTimetableCacheKeys(params)],
    () => getTimetable(params),
    options,
  );
};

export const CREATE_TIMETABLE_MUTATION = `${TIMETABLES_URL}CREATE_TIMETABLE_MUTATION`;

export function createTimetable(data: CreateTimetableRequest): Promise<CreateTimetableResponse> {
  return api.post(`${SCHOOLY_API_V2_URL}${createTimetablePath()}`, data);
}

export const useCreateTimetableMutation = (
  options?: RQUseMutationOptions<CreateTimetableResponse, CreateTimetableRequest>,
): RQUseMutationResult<CreateTimetableResponse, CreateTimetableRequest> => {
  return useMutation(
    [CREATE_TIMETABLE_MUTATION],
    (params: CreateTimetableRequest) => createTimetable(params),
    options,
  );
};

export const MODIFY_TIMETABLE_MUTATION = `${TIMETABLES_URL}MODIFY_TIMETABLE_MUTATION`;

export function modifyTimetable({
  params,
  data,
}: ModifyTimetableRequest): Promise<ModifyTimetableResponse> {
  return api.patch(
    `${SCHOOLY_API_V2_URL}${modifyTimetablePath({ timetable_id: params.timetableId })}`,
    data,
  );
}

export const useModifyTimetableMutation = (
  options?: RQUseMutationOptions<ModifyTimetableResponse, ModifyTimetableRequest>,
): RQUseMutationResult<ModifyTimetableResponse, ModifyTimetableRequest> => {
  return useMutation(
    [MODIFY_TIMETABLE_MUTATION],
    (params: ModifyTimetableRequest) => modifyTimetable(params),
    options,
  );
};

export const DELETE_TIMETABLE_MUTATION = `${TIMETABLES_URL}DELETE_TIMETABLE_MUTATION`;

export function deleteTimetable({
  timetableId,
}: DeleteTimetableRequest): Promise<DeleteTimetableResponse> {
  return api.remove(`${SCHOOLY_API_V2_URL}${deleteTimetablePath({ timetable_id: timetableId })}`);
}

export const useDeleteTimetableMutation = (
  options?: RQUseMutationOptions<DeleteTimetableResponse, DeleteTimetableRequest>,
): RQUseMutationResult<DeleteTimetableResponse, DeleteTimetableRequest> => {
  return useMutation(
    [DELETE_TIMETABLE_MUTATION],
    (params: DeleteTimetableRequest) => deleteTimetable(params),
    options,
  );
};

export const PERFORM_TIMETABLE_ACTION_MUTATION = `${TIMETABLES_URL}PERFORM_TIMETABLE_ACTION_MUTATION`;

export function performTimetableAction({
  params,
  data,
}: PerformTimetableActionRequest): Promise<PerformTimetableActionResponse> {
  return api.post(
    `${SCHOOLY_API_V2_URL}${performTimetableActionPath({ timetable_id: params.timetableId })}`,
    data,
  );
}

export const usePerformTimetableActionMutation = (
  options?: RQUseMutationOptions<PerformTimetableActionResponse, PerformTimetableActionRequest>,
): RQUseMutationResult<PerformTimetableActionResponse, PerformTimetableActionRequest> => {
  return useMutation(
    [PERFORM_TIMETABLE_ACTION_MUTATION],
    (params: PerformTimetableActionRequest) => performTimetableAction(params),
    options,
  );
};

export const GET_STUDENT_SCHEDULE_QUERY = `${TIMETABLES_URL}GET_STUDENT_SCHEDULE_QUERY`;

export function getStudentSchedule({
  studentId,
}: GetStudentScheduleRequest): Promise<GetStudentScheduleResponse> {
  return api.get(`${SCHOOLY_API_V2_URL}${getStudentSchedulePath({ student_id: studentId })}`);
}

export const useGetStudentScheduleQuery = (
  params: GetStudentScheduleRequest,
  options?: RQUseQueryOptions<GetStudentScheduleResponse>,
) => {
  return useQuery<GetStudentScheduleResponse, ApiError>(
    [GET_STUDENT_SCHEDULE_QUERY, params],
    () => getStudentSchedule(params),
    options,
  );
};

export const GET_STAFF_SCHEDULE_QUERY = `${TIMETABLES_URL}GET_STAFF_SCHEDULE_QUERY`;

export async function getStaffSchedule(
  params: GetStaffScheduleRequest,
): Promise<GetStaffScheduleResponse> {
  return api.get(`${SCHOOLY_API_V2_URL}${getStaffSchedulePath({ staff_id: params.staffId })}`);
}

export const useGetStaffScheduleQuery = (
  params: GetStaffScheduleRequest,
  options?: RQUseQueryOptions<GetStaffScheduleResponse>,
) => {
  return useQuery<GetStaffScheduleResponse, ApiError>(
    [GET_STAFF_SCHEDULE_QUERY, params],
    () => getStaffSchedule(params),
    options,
  );
};

export const GET_GROUP_SCHEDULE_QUERY = `${TIMETABLES_URL}GET_GROUP_SCHEDULE_QUERY`;

export function getGroupSchedule({
  groupId,
}: GetGroupScheduleRequest): Promise<GetGroupScheduleResponse> {
  return api.get(`${SCHOOLY_API_V2_URL}${getGroupSchedulePath({ group_id: groupId })}`);
}

export const useGetGroupScheduleQuery = (
  params: GetGroupScheduleRequest,
  options?: RQUseQueryOptions<GetGroupScheduleResponse>,
) => {
  return useQuery<GetGroupScheduleResponse, ApiError>(
    [GET_GROUP_SCHEDULE_QUERY, params],
    () => getGroupSchedule(params),
    options,
  );
};
